import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

// Material UI Components
import { makeStyles } from "@mui/styles";

// Core Components
import MDCButton from "../../core/MDCButton";
import MDCLabel from "../../core/MDCLabel";

// Theme
import theme from "../../../themes/mdc-theme";

// Constants
import { QES_VENDOR_SELECTION } from "../../../constants/PluginConstants";

const useStyles = makeStyles(() => ({
  vendorSection: {
    width: "100%",
    textAlign: "left",
    font: "normal normal normal 13px/15px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.color18,
    opacity: 1,
  },
  sectionTitle: {
    textAlign: "left",
    font: "normal normal 800 14px/17px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
    opacity: 1,
  },
  sectionDescription: {
    margin: "0.25rem 0 0",
    color: theme.palette.MDCColors.color18,
    fontSize: "13px",
    textAlign: "left",
    font: "normal normal normal 13px/15px Fira Sans",
    letterSpacing: "0px",
    opacity: 1,
    fontFamily: theme.palette.typography.fontFamily,
  },
  vendorStack: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "100%",
    marginTop: "0.75rem",
  },
  vendorCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 14px",
    width: "100%",
    boxSizing: "border-box",
    background: theme.palette.MDCColors.white,
    border: `1px solid ${theme.palette.MDCColors.color21}`,
    borderRadius: "8px",
    // MDCButton's &.MuiButton-* rules are (0,2,0) — !important is required to
    // slim the button from inside this card; MDCButton itself stays untouched
    "& button": {
      whiteSpace: "nowrap",
      flexShrink: 0,
      margin: 0,
      padding: "4px 14px !important",
      fontSize: "13px !important",
      minWidth: "unset !important",
    },
  },
  vendorIdentity: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  },
  vendorText: {
    minWidth: 0,
  },
  vendorLogo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "72px",
    height: "32px",
    borderRadius: "6px",
    flexShrink: 0,
    color: theme.palette.MDCColors.white,
    backgroundColor: (props) =>
      `${props.providerDetails?.colorCode} !important`,
    fontWeight: 800,
    fontSize: "14px",
    fontFamily: theme.palette.typography.fontFamily,
  },
  vendorName: {
    margin: 0,
    color: theme.palette.MDCColors.textColor,
    fontWeight: 700,
    fontSize: "14px",
    fontFamily: theme.palette.typography.fontFamily,
    textAlign: "left",
    font: "normal normal bold 16px/19px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
    opacity: 1,
  },
  vendorDescription: {
    margin: "2px 0 0",
    color: theme.palette.MDCColors.color18,
    fontSize: "12px",
    fontFamily: theme.palette.typography.fontFamily,
    // Long descriptions clamp instead of inflating the card
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
}));

const QESVendorCard = ({
  providerDetails,
  onStart,
  qesValidationResult,
  isPPOA,
  disabled,
}) => {
  const classes = useStyles({ providerDetails });

  return (
    <div className={classes.vendorCard}>
      <div className={classes.vendorIdentity}>
        <div className={classes.vendorLogo}>{providerDetails?.displayName}</div>
        <div className={classes.vendorText}>
          {/* <p className={classes.vendorName}>{providerDetails?.displayName}</p> */}
          <p className={classes.vendorDescription}>
            {providerDetails?.description}
          </p>
        </div>
      </div>
      <MDCButton
        variant="outlined"
        label={QES_VENDOR_SELECTION.START_SIGNING}
        disabled={disabled || (!isPPOA && !qesValidationResult)}
        onClick={() => onStart(providerDetails)}
      />
    </div>
  );
};

QESVendorCard.propTypes = {
  providerDetails: PropTypes.object.isRequired,
  isStarting: PropTypes.bool,
  disabled: PropTypes.bool,
  qesValidationResult: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onStart: PropTypes.func.isRequired,
  isPPOA: PropTypes.bool,
};

const ProviderSelection = ({
  disabled,
  qesValidationResult,
  onStartSigning,
  isPPOA,
}) => {
  const classes = useStyles();
  const [startingVendorKey, setStartingVendorKey] = useState(null);

  const qesProviders = useSelector(
    (state) => state?.pluginReducer?.qesProviders
  );

  const handleStart = (providerDetails) => {
    setStartingVendorKey(providerDetails.providerId);
    if (onStartSigning) {
      onStartSigning(providerDetails);
    }
  };

  return (
    <div className={classes.vendorSection}>
      <MDCLabel
        className={classes.sectionTitle}
        label={QES_VENDOR_SELECTION.SECTION_TITLE}
      />
      <p className={classes.sectionDescription}>
        {QES_VENDOR_SELECTION.SECTION_DESCRIPTION}
      </p>
      <div className={classes.vendorStack}>
        {qesProviders?.map((providerDetails) => (
          <QESVendorCard
            key={providerDetails?.providerId}
            providerDetails={providerDetails}
            isStarting={startingVendorKey === providerDetails?.providerId}
            disabled={disabled || startingVendorKey !== null}
            qesValidationResult={qesValidationResult}
            onStart={handleStart}
            isPPOA={isPPOA}
          />
        ))}
      </div>
    </div>
  );
};

ProviderSelection.propTypes = {
  requestId: PropTypes.string,
  disabled: PropTypes.bool,
  qesValidationResult: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  startSigning: PropTypes.func,
  onStartSigning: PropTypes.func,
};

export default React.memo(ProviderSelection);
